import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { GlobalModalsType } from '@app/routing/GlobalModals'

import { useTraderRoomRoutesConditions } from '@pages/Traders-Room/TradersRoomRoutes'

import { useAccountInfo } from '@context/AccountInfoContext/AccountInfoContext.Provider'
import { useMenu } from '@context/MenuContext'
import { useModalsContext } from '@context/ModalsContext'
import { useSessionLanguage } from '@context/SessionSettingsContext'

import { HomeIcon } from '@icons/HomeIcon'

import { useApiClient } from '@utils/ApiClient'
import { ClientApiClient } from '@utils/clientApi'

import { TickmillProductType } from '@model/TickmillProductType'

import { useLuckyDraw } from '@global/fortuneWheel/useLuckyDraw'

import { useFetchOne } from '@hooks/useFetch'
import { useLocallyPersistedState } from '@hooks/useStorage'

import { MenuSection } from '../useMenuSections'

export const useTradersRoomMenu = (): MenuSection => {
  const { t } = useTranslation()
  const [luckyDrawEligible] = useLocallyPersistedState('luckyDrawEligible', false)

  const apiClient = useApiClient(ClientApiClient)
  const locale = useSessionLanguage()

  const callback = useMemo(
    () => async () => apiClient.getClientCampaigns(locale),
    [apiClient, locale]
  )
  const listCallback = useMemo(() => async () => apiClient.getClientCampaignsList(), [apiClient])

  const { data: campaigns } = useFetchOne(callback)
  const { data: campaignsList } = useFetchOne(listCallback)

  const { isTradersRoomVisible } = useTraderRoomRoutesConditions()
  const { setShowLuckyDraw, luckyDrawCampaign } = useLuckyDraw()
  const { setActiveModal } = useModalsContext()

  const { closeMenu } = useMenu()
  const { accountInfoLimits } = useAccountInfo()

  return useMemo(
    () => ({
      id: 'traders-room',
      label: t('Tabs.Traders room'),
      icon: HomeIcon,
      condition: isTradersRoomVisible,
      children: [
        {
          id: 'wallets',
          label: t('Tabs.Wallets'),
          path: '/dashboard/traders-room/wallets',
          products: [TickmillProductType.CFD],
          children: [
            {
              id: 'deposit',
              label: 'Deposit',
              path: '/dashboard/traders-room/wallets/deposit',
            },
            {
              id: 'withdraw',
              label: 'Withdraw',
              path: '/dashboard/traders-room/wallets/withdrawal',
            },
            {
              id: 'transfer',
              label: 'Transfer',
              path: '/dashboard/traders-room/wallets/transfer',
            },
          ],
        },
        {
          id: 'balances',
          label: t('Tabs.Balances'),
          path: '/dashboard/traders-room/balances',
          products: [TickmillProductType.ETD],
        },
        {
          id: 'transactions',
          label: t('Tabs.Transactions'),
          path: '/dashboard/traders-room/transactions',
        },
        {
          id: 'trading-accounts',
          label: t('Tabs.Trading Accounts'),
          path: '/dashboard/traders-room/trading-accounts',
          products: [TickmillProductType.CFD],
        },
        {
          id: 'transaction-history',
          label: t('Tabs.Transaction History'),
          path: '/dashboard/traders-room/transaction-history',
        },
        {
          id: 'stock-dividends',
          label: t('Tabs.Stock Dividends'),
          path: '/dashboard/traders-room/stock-dividends',
          products: [TickmillProductType.CFD],
        },
        {
          id: 'campaign-results',
          label: t('Tabs.Campaign Results'),
          path: '/dashboard/traders-room/campaign-results',
          condition: !!campaignsList?.items.length,
          products: [TickmillProductType.CFD],
        },
        {
          id: 'add-campaign-account',
          label: t('Tabs.Add Campaign Account'),
          condition: !!campaigns?.length && !accountInfoLimits?.hasReachedMaxTradingAccounts,
          highlighted: true,
          onClick: () => {
            setActiveModal(GlobalModalsType.AddCampaignAccount)
          },
          products: [TickmillProductType.CFD],
        },
        {
          id: 'lucky-draw',
          label: t('Tabs.Lucky Draw'),
          onClick: () => {
            closeMenu()
            setShowLuckyDraw(true)
          },
          highlighted: true,
          state: { luckyDrawModalActive: true },
          condition: luckyDrawEligible && !!luckyDrawCampaign,
          products: [TickmillProductType.CFD],
        },
      ],
    }),
    [
      t,
      isTradersRoomVisible,
      campaigns?.length,
      campaignsList?.items.length,
      luckyDrawEligible,
      luckyDrawCampaign,
      closeMenu,
      setShowLuckyDraw,
    ]
  )
}
